/*
 * Copyright (c) Nexign, JSC, 2020-2021.
 */
import { createRef, PureComponent, ReactElement } from 'react';

import Collapse from '@material-ui/core/Collapse';
/**
 * Panel component for Collapse
 *
 * @author Yakov Gushchin
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @version 1.13.0
 * @since 1.3.1
 */
import classNames from 'classnames';
import invoke from 'lodash/invoke';

import { BlockWithIcon } from '../index';
import { PanelBodyStyled, PanelContainerStyled, PanelTitleStyled } from './styled/CollapsePanelStyled';

/**
 * @description Panel props for styling component and set header values.
 * @interface ICollapsePanelProps
 *
 * @property{string} bodyStyle Define style props for panel body container.
 * @property{string} iconClosed Define icon path when collapse is closed.
 * @property{string} iconOpened Define icon path when collapse is opened.
 * @property{number} iconSize Define icon size.
 * @property{boolean} isOpen Define visibility of current panel.
 * @property{string} panelContainerStyle Define style props for panel container.
 * @property{string} titleStyle Define style props for panel title container.
 * @property{string} titleValue Define title value.
 *
 * @see Collapse component For all panels.
 * @see CollapsePanel component For panels in collapse.
 * @see ICollapseProps interface Props for Collapse container.
 */
export interface ICollapsePanelProps {
    bodyStyle: string;
    icon: string;
    iconSize: number;
    isOpen?: boolean;
    onClick?: (isOpen: boolean) => void;
    panelContainerStyle: string;
    titleStyle: string;
    titleValue: string | ReactElement;
}

/**
 * @description Define state interface for CollapsePanel component.
 * @interface ICollapsePanelState
 *
 * @property{boolean} isOpen Define visibility of current panel.
 */
interface ICollapsePanelState {
    isOpen: boolean;
}

/**
 * @description Component for render collapse Panel (Open-close on click).
 * @class CollapsePanel
 * @extends PureComponent<ICollapsePanelProps>
 *
 * @see BlockWithIcon Component for render block with icons.
 * @see PanelBody Styled div for render panel body.
 * @see PanelContainer Styled div for render panel container.
 * @see PanelTitle Styled div for render panel title.
 */
class CollapsePanel extends PureComponent<ICollapsePanelProps, ICollapsePanelState> {

    public static defaultProps = {
        bodyStyle: '',
        icon: '',
        iconSize: 32,
        panelContainerStyle: '',
        titleStyle: '',
    };

    public panelBodyRef = createRef<HTMLDivElement>();

    public state = {
        isOpen: this.props.isOpen || false,
    };

    /**
     * @description On panel click handler to change state.
     * @return{undefined}
     */
    public onClickHandler = () => {
        this.setState(state => ({
            isOpen: !state.isOpen,
        }));
        invoke(this.props, 'onClick', !this.state.isOpen);
    };

    public render() {
        const {
            bodyStyle,
            icon,
            iconSize,
            panelContainerStyle,
            titleStyle,
            titleValue,
        } = this.props;

        const {
            isOpen,
        } = this.state;

        const classes = classNames({
            opened: this.state.isOpen,
            disabled: !this.props.children,
        });

        return (
            <PanelContainerStyled styleProps={panelContainerStyle}>

                <PanelTitleStyled
                    className={classes}
                    styleProps={titleStyle}
                    onClick={() => this.onClickHandler()}>
                    <BlockWithIcon
                        iconRight={icon}
                        iconRightSize={iconSize}
                        justifyContent="space-between"
                    >
                        {titleValue}
                    </BlockWithIcon>
                </PanelTitleStyled>

                <Collapse in={isOpen}>
                    <PanelBodyStyled
                        ref={this.panelBodyRef}
                        styleProps={bodyStyle}>
                        {this.props.children}
                    </PanelBodyStyled>
                </Collapse>

            </PanelContainerStyled>
        );
    }
}

export default CollapsePanel;
